(() => {
  if (window.HighSpiritsPerksLoaded) return;
  window.HighSpiritsPerksLoaded = true;

  const GUEST_STATE_KEY = "highSpiritsPerksGuestState";
  const SUBSCRIBED_EVENT = "high-spirits:marketing-subscribed";
  const STATUS_EVENT = "high-spirits:member-status";
  const CART_STATE_ATTRIBUTE = "_high_spirits_perks_state";

  // Remember only drawer UI and the explicit action that started native sign-in.
  // This is a short-lived, same-tab return marker, never a membership/auth cache.
  const CART_SIGNIN_KEY = "highSpiritsCartSignInReturn";
  const CART_SIGNIN_TTL_MS = 5 * 60 * 1000;
  const CART_SIGNIN_SETTLE_MS = 15 * 1000;
  let cartReturn = readCartReturn();
  let cartNeedsResume = Boolean(cartReturn && cartReturn.phase === "pending");
  let cartNeedsConsume = Boolean(cartReturn && cartReturn.phase === "complete");
  let cartAuthRunning = false;
  let cartPageLeaving = false;
  let cartRestoreQueued = false;
  let cartConsumeTimer = null;

  function validCartReturn(record) {
    return Boolean(record && record.version === 1 && typeof record.id === "string" &&
      record.path === window.location.pathname &&
      ["signin", "join", "subscribe"].includes(record.action) &&
      ["pending", "complete"].includes(record.phase) &&
      Number.isFinite(record.expiresAt) && record.expiresAt > Date.now() &&
      record.expiresAt <= Date.now() + CART_SIGNIN_TTL_MS);
  }

  function readCartReturn() {
    try {
      const record = JSON.parse(sessionStorage.getItem(CART_SIGNIN_KEY) || "null");
      if (validCartReturn(record)) return record;
      sessionStorage.removeItem(CART_SIGNIN_KEY);
    } catch (error) {
      try { sessionStorage.removeItem(CART_SIGNIN_KEY); } catch (ignored) {}
    }
    return null;
  }

  function writeCartReturn(record) {
    cartReturn = record;
    try { sessionStorage.setItem(CART_SIGNIN_KEY, JSON.stringify(record)); } catch (error) {}
  }

  function clearCartReturn(id) {
    if (id && cartReturn?.id !== id) return;
    cartReturn = null;
    cartNeedsResume = false;
    cartNeedsConsume = false;
    window.clearTimeout(cartConsumeTimer);
    try { sessionStorage.removeItem(CART_SIGNIN_KEY); } catch (error) {}
  }

  function drawerParts() {
    const drawer = document.getElementById("site-cart-sidebar");
    const perks = drawer?.querySelector('high-spirits-perks[data-surface="cart_drawer"]');
    return { drawer, perks, accordion: perks?.querySelector("[data-hs-perks-accordion]") };
  }

  function drawerIsOpen(drawer) {
    return Boolean(drawer && (drawer.opened === true || drawer.classList.contains("sidebar--opened")));
  }

  function rememberCartSignIn(owner, { activateClub, subscribeIfNeeded }) {
    const { drawer, perks, accordion } = drawerParts();
    if (owner !== perks || !drawerIsOpen(drawer)) return null;
    const record = {
      version: 1,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      path: window.location.pathname,
      phase: "pending",
      action: subscribeIfNeeded ? "subscribe" : activateClub ? "join" : "signin",
      expiresAt: Date.now() + CART_SIGNIN_TTL_MS,
      perksOpen: accordion?.querySelector("[data-js-title]")?.getAttribute("aria-expanded") === "true",
      scrollTop: drawer.scrollTop,
      bodyScrollTop: drawer.querySelector(".sidebar__body")?.scrollTop || 0,
    };
    window.clearTimeout(cartConsumeTimer);
    cartNeedsResume = false;
    cartNeedsConsume = false;
    cartAuthRunning = true;
    writeCartReturn(record);
    return record;
  }

  function restoreCartUI(record) {
    if (!validCartReturn(record) || cartReturn?.id !== record.id || cartPageLeaving) return false;
    const { drawer, perks, accordion } = drawerParts();
    if (!drawer || !perks || typeof drawer.show !== "function" ||
        typeof perks.startNativeAccountFlow !== "function" ||
        (record.perksOpen && typeof accordion?.onClickHandler !== "function")) return false;

    // Use the theme's own methods, preserving its overlay, inert handling,
    // animations and exact Product Info accordion/chevron implementation.
    if (!drawerIsOpen(drawer)) drawer.show();
    document.querySelectorAll('[data-js-sidebar-handle][aria-controls="site-cart-sidebar"]').forEach((handle) => {
      handle.setAttribute("aria-expanded", "true");
    });
    if (record.perksOpen && accordion.querySelector("[data-js-title]")?.getAttribute("aria-expanded") !== "true") {
      accordion.onClickHandler();
    }
    const restoreScroll = () => {
      if (cartReturn?.id !== record.id || !drawer.isConnected || !drawerIsOpen(drawer)) return;
      drawer.scrollTop = Number.isFinite(record.scrollTop) ? record.scrollTop : 0;
      const body = drawer.querySelector(".sidebar__body");
      if (body) body.scrollTop = Number.isFinite(record.bodyScrollTop) ? record.bodyScrollTop : 0;
    };
    requestAnimationFrame(restoreScroll);
    window.setTimeout(restoreScroll, 220);
    return true;
  }

  function queueCartReturn() {
    if (cartRestoreQueued || cartPageLeaving || !cartReturn) return;
    cartRestoreQueued = true;
    requestAnimationFrame(() => {
      cartRestoreQueued = false;
      const record = cartReturn;
      if (!validCartReturn(record)) {
        clearCartReturn();
        return;
      }
      if (!restoreCartUI(record) || cartAuthRunning) return;
      const { perks } = drawerParts();
      if (cartNeedsResume) {
        cartNeedsResume = false;
        // Never reopen Shopify's sign-in sheet automatically after a return.
        // Resume only against a fresh authenticated response from the backend.
        void perks.startNativeAccountFlow({
          activateClub: record.action !== "signin",
          subscribeIfNeeded: record.action === "subscribe",
          resumeCartReturn: record,
        });
      } else if (cartNeedsConsume) {
        cartNeedsConsume = false;
        // A completed flow needs UI restoration only, not another activation.
        cartConsumeTimer = window.setTimeout(() => clearCartReturn(record.id), 260);
      }
    });
  }

  function finishCartSignIn(record, signedIn) {
    cartAuthRunning = false;
    if (!record || cartReturn?.id !== record.id || cartPageLeaving) return;
    restoreCartUI(record);
    if (!signedIn) {
      // Keep the currently open drawer, but do not reopen it on a later visit
      // after cancelled sign-in, an error, or a signed-out return.
      cartConsumeTimer = window.setTimeout(() => clearCartReturn(record.id), 260);
      return;
    }
    // Shopify can refresh immediately AFTER its session becomes observable.
    // Retain one brief UI-only return marker to cover that timing window.
    writeCartReturn({ ...record, phase: "complete", expiresAt: Date.now() + CART_SIGNIN_SETTLE_MS });
    cartConsumeTimer = window.setTimeout(() => clearCartReturn(record.id), CART_SIGNIN_SETTLE_MS);
  }

  // Explicit close always wins. Never force the drawer back open after the
  // shopper closes it, dismisses it with Escape, or chooses its overlay.
  document.addEventListener("click", (event) => {
    if (!cartReturn || !(event.target instanceof Element)) return;
    if (event.target.closest('#site-cart-sidebar [data-js-close], .site-overlay')) clearCartReturn();
  }, true);
  document.addEventListener("keydown", (event) => {
    if (cartReturn && (event.key === "Escape" || event.keyCode === 27)) clearCartReturn();
  }, true);
  window.addEventListener("pagehide", () => {
    cartPageLeaving = true;
    if (!validCartReturn(cartReturn)) return;
    const { drawer } = drawerParts();
    if (drawerIsOpen(drawer)) {
      writeCartReturn({
        ...cartReturn,
        scrollTop: drawer.scrollTop,
        bodyScrollTop: drawer.querySelector(".sidebar__body")?.scrollTop || 0,
      });
    }
  });
  window.addEventListener("pageshow", () => {
    cartPageLeaving = false;
    queueCartReturn();
  });
  customElements.whenDefined("sidebar-drawer").then(queueCartReturn);
  customElements.whenDefined("toggle-tab").then(queueCartReturn);

  class HighSpiritsPerks extends HTMLElement {
    connectedCallback() {
      // Moving the PDP element can synchronously reconnect it. Bind DOM handlers
      // only once, while allowing document handlers to reconnect after removal.
      if (this._hsConnecting) return;
      this._hsConnecting = true;
      try {
        this.cacheElements();
        this.positionSurface();

        if (!this._hsListenersBound) {
          this._hsListenersBound = true;
          this.addEventListener("submit", (event) => {
            if (event.target.matches("[data-hs-perks-details-form]")) {
              this.beginShopifyAuth(event);
            }
          });
          this.addEventListener("click", (event) => {
            if (event.target.closest("[data-hs-perks-subscribe]")) this.activateLoggedInCustomer();
            if (event.target.closest("[data-hs-perks-reset]")) this.resetGuestFlow();
            if (event.target.closest("[data-hs-perks-shopify-signin]")) {
              event.preventDefault();
              this.beginShopifyAuthFromLink();
            }
          });
        }

        if (!this._hsSubscribedHandler) {
          // An event (including a newsletter-form event) is not proof that the
          // current storefront visitor is an authenticated club member.
          this._hsSubscribedHandler = () => this.hydrateState();
          document.addEventListener(SUBSCRIBED_EVENT, this._hsSubscribedHandler);
        }
        this.hydrateState();
      } finally {
        this._hsConnecting = false;
      }
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
        if (this._hsSubscribedHandler) {
          document.removeEventListener(SUBSCRIBED_EVENT, this._hsSubscribedHandler);
          this._hsSubscribedHandler = null;
        }
      });
    }

    positionSurface() {
      if (this.dataset.surface !== "product") return;
      const productRoot = this.closest("product-page") || document.querySelector("product-page");
      if (!productRoot) return;
      const price =
        productRoot.querySelector('[data-update-block="price-compact"]') ||
        productRoot.querySelector('[data-update-block="price-extended"]');
      if (price) {
        if (price.nextElementSibling !== this) {
          price.insertAdjacentElement("afterend", this);
        }
        this.classList.add("hs-perks--positioned");
      }
    }

    cacheElements() {
      this.proxyUrl = this.dataset.proxyUrl || "/apps/high-spirits-welcome";
      this.titleElement = this.querySelector("[data-hs-perks-title]");
      this.status = this.querySelector("[data-hs-perks-status]");
      this.detailsStep = this.querySelector('[data-hs-perks-step="details"]');
      this.resultStep = this.querySelector('[data-hs-perks-step="result"]');
      this.detailsForm = this.querySelector("[data-hs-perks-details-form]");
      this.subscribeButton = this.querySelector("[data-hs-perks-subscribe]");
      this.accordion = this.querySelector("[data-hs-perks-accordion]");
    }

    async hydrateState() {
      const version = this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
      try {
        const status = await this.memberStatus();
        // A late response must not undo a newer status check or user action.
        if (!this.isConnected || version !== this._hsHydrationVersion) return;
        this.applyMemberStatus(status);
      } catch (error) {
        if (!this.isConnected || version !== this._hsHydrationVersion) return;
        // Never use a saved guest/cart flag as authentication on a failed check.
        this.setSignedOutState({ confirmed: false });
        console.warn("High Spirits: couldn't confirm Shopify customer status.", error);
      }
    }

    applyMemberStatus(status) {
      if (status?.ok !== true || typeof status.authenticated !== "boolean") {
        throw new Error("We couldn't confirm your Shopify account. Please try again.");
      }

      if (!status.authenticated) {
        this.setSignedOutState({ confirmed: true });
      } else {
        this.clearLegacyGuestState();
        this._hsAuthenticated = true;
        this._hsAuthenticatedEmail = status.email || null;
        this.setState(status.member && status.subscribed ? "subscribed" : "unsubscribed");
      }

      // This broadcasts a fresh result, not a cached result. It also lets the
      // quiz clear a previously saved subscription flag after sign-out.
      document.dispatchEvent(new CustomEvent(STATUS_EVENT, {
        detail: {
          authenticated: status.authenticated,
          subscribed: status.authenticated && status.subscribed === true,
          member: status.authenticated && status.member === true,
        },
      }));
    }

    clearLegacyGuestState() {
      try { localStorage.removeItem(GUEST_STATE_KEY); } catch (error) {}
      try { sessionStorage.removeItem(GUEST_STATE_KEY); } catch (error) {}
      this.removeAttribute("data-cart-perks-state");
      // Do not remove cart discounts or alter Shopify marketing consent here.
      // A historical cart attribute is simply no longer an auth signal.
    }

    setSignedOutState({ confirmed = true } = {}) {
      const hadAccount = this._hsAuthenticated === true || this.dataset.state !== "guest" ||
        (this.resultStep && !this.resultStep.hidden);
      if (confirmed && hadAccount) {
        this._hsSessionVersion = (this._hsSessionVersion || 0) + 1;
      }
      this._hsAuthenticated = false;
      this._hsAuthenticatedEmail = null;
      const feedback = this.querySelector("[data-hs-perks-activation-feedback]");
      if (feedback) {
        feedback.hidden = true;
        if (feedback.childNodes.length) feedback.replaceChildren();
      }
      this.clearLegacyGuestState();
      this.setState("guest");
      if (this.detailsStep) this.detailsStep.hidden = false;
      if (this.resultStep) {
        this.resultStep.hidden = true;
        if (this.resultStep.childNodes.length) this.resultStep.replaceChildren();
      }
      // A signed-out response is expected while the native account sheet is
      // open. Do not reset the in-progress button or error state in that case.
      if (!this._hsAuthInProgress) {
        if (confirmed && hadAccount) {
          this.detailsForm?.reset();
          this.querySelectorAll("[data-hs-perks-error]").forEach((error) => {
            error.textContent = "";
            error.hidden = true;
          });
        }
      }
    }

    async persistGuestCartState(state, discountCode = null) {
      const root = window.Shopify?.routes?.root || "/";
      const cartUpdateUrl =
        window.KROWN?.settings?.routes?.cart_update_url || `${root}cart/update.js`;

      const payload = {
        attributes: {
          [CART_STATE_ATTRIBUTE]: state,
        },
      };

      if (discountCode) payload.discount = discountCode;

      const response = await fetch(cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });

      const cart = await response.json().catch(() => null);
      if (!response.ok || !cart) {
        throw new Error("Your perks are active, but we couldn't update your cart. Please refresh and try again.");
      }

      return cart;
    }

    async applyDiscountCode(discountCode) {
      const cart = await this.persistGuestCartState("welcome_applied", discountCode);

      // Refresh the theme's AJAX cart so the newly applied discount and perks
      // state are visible immediately in the cart drawer.
      if (typeof window.renderHelperCart === "function" && document.getElementById("AjaxCartForm")) {
        try {
          const sectionUrl = new URL(window.location.href);
          sectionUrl.searchParams.set("section_id", "helper-cart");
          const sectionResponse = await fetch(
            `${sectionUrl.pathname}${sectionUrl.search}`,
            { credentials: "same-origin" },
          );

          if (sectionResponse.ok) {
            const sectionHtml = await sectionResponse.text();
            window.renderHelperCart(sectionHtml);

            const cartForm = document.getElementById("AjaxCartForm");
            cartForm?.dispatchEvent(new Event("cart-updated", { bubbles: true }));
          }
        } catch (error) {
          console.warn("High Spirits: discount applied, but cart drawer refresh failed.", error);
        }
      }

      return cart;
    }

    async api(path, options = {}) {
      const headers = { Accept: "application/json", ...(options.headers || {}) };
      if (options.body) headers["Content-Type"] = "application/json";
      const response = await fetch(`${this.proxyUrl}${path}`, {
        ...options,
        headers,
        credentials: "same-origin",
      });

      const data = await response.json().catch(() => ({ message: "Invalid server response." }));
      if (!response.ok) {
        const error = new Error(data.message || "Request failed.");
        error.code = data.code || "REQUEST_FAILED";
        throw error;
      }
      return data;
    }

    errorElement(scope) {
      return scope?.querySelector("[data-hs-perks-error]");
    }

    showError(scope, message) {
      const error = this.errorElement(scope);
      if (!error) return;
      if (this.accordion && !this.accordion.classList.contains("opened")) {
        this.accordion.onClickHandler?.();
      }
      error.textContent = message;
      error.hidden = false;
    }

    clearError(scope) {
      const error = this.errorElement(scope);
      if (!error) return;
      error.textContent = "";
      error.hidden = true;
    }

    setButtonBusy(button, busy, busyText) {
      if (!button) return;
      const text = button.querySelector("[data-hs-perks-button-text]") || button;
      if (busy) {
        if (!button.dataset.originalText) button.dataset.originalText = text.textContent;
        text.textContent = busyText;
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      } else {
        text.textContent = button.dataset.originalText || text.textContent;
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }
    }

    stateTitle(state) {
      if (state === "subscribed") return "High Spirits Club perks active — 2.5% cashback + $5 bonus";
      if (state === "unsubscribed") return "Join the High Spirits Club — $5 welcome offer + cashback";
      return "Join the High Spirits Club — $5 welcome offer + cashback";
    }

    setState(state) {
      this.dataset.state = state;
      this.querySelectorAll("[data-hs-perks-view]").forEach((view) => {
        view.hidden = view.dataset.hsPerksView !== state;
      });

      if (this.status) this.status.hidden = state !== "subscribed";
      const title = this.stateTitle(state);
      if (this.titleElement && this.titleElement.textContent !== title) {
        this.titleElement.textContent = title;
      }
    }

    async memberStatus() {
      return this.api("/member-status", { method: "POST", cache: "no-store" });
    }

    getAccountComponent() {
      const accounts = Array.from(document.querySelectorAll("shopify-account"));
      if (!accounts.length) return null;

      // Prefer the account control that is actually visible at the current
      // breakpoint. Both desktop and mobile controls can exist in the DOM.
      return (
        accounts.find((account) => {
          const parent = account.closest(".button--account-handle, .mobile-account-button") || account;
          const rect = parent.getBoundingClientRect();
          const style = window.getComputedStyle(parent);
          return rect.width > 0 && rect.height > 0 && style.display !== "none" && style.visibility !== "hidden";
        }) || accounts[0]
      );
    }

    openShopifyAccount() {
      const account = this.getAccountComponent();
      if (!account) {
        throw new Error("Shopify account sign-in is unavailable on this page. Please refresh and try again.");
      }

      // Shopify's account web component owns the passwordless sign-in sheet.
      // Clicking its slotted signed-out avatar opens the native sheet without
      // navigating away from the storefront or constructing redirect URLs.
      const trigger = account.querySelector('[slot="signed-out-avatar"]');
      if (trigger && typeof trigger.click === "function") {
        trigger.click();
      } else if (typeof account.click === "function") {
        account.click();
      } else {
        throw new Error("Shopify account sign-in could not be opened. Please try again.");
      }

      return account;
    }

    async beginShopifyAuth(event) {
      event.preventDefault();
      // Shopify collects the email and checkbox consent. Join does not opt a
      // shopper in behind the scenes if they leave that checkbox unchecked.
      return this.startNativeAccountFlow({ activateClub: true });
    }

    async beginShopifyAuthFromLink() {
      // Sign in only authenticates. A previously unsubscribed customer must
      // explicitly click Subscribe & unlock rewards to opt back into marketing.
      return this.startNativeAccountFlow({ activateClub: false });
    }

    async startNativeAccountFlow({ activateClub = false, subscribeIfNeeded = false, scope, button, resumeCartReturn = null } = {}) {
      if (this._hsAuthInProgress) return;
      scope = scope || this.detailsStep;
      button = button || this.detailsForm?.querySelector('[type="submit"]');
      this._hsAuthInProgress = true;
      this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
      this.clearError(scope);
      this.setButtonBusy(button, true, "Complete sign-in...");
      const cartFlow = resumeCartReturn || rememberCartSignIn(this, { activateClub, subscribeIfNeeded });
      if (cartFlow) cartAuthRunning = true;
      let signedIn = false;
      let cartHandedOff = false;
      let failure = null;

      try {
        let status = await this.memberStatus();
        if (status?.ok !== true) throw new Error("We couldn't confirm your Shopify account. Please try again.");
        if (!this.isConnected) return;

        if (!status.authenticated) {
          if (resumeCartReturn) {
            this.applyMemberStatus(status);
            return;
          }
          const account = this.openShopifyAccount();
          status = await this.waitForShopifyAuthentication(account, cartFlow);
          if (!status.authenticated) {
            throw new Error("Sign-in wasn't completed. Please try again.");
          }
        }

        if (!this.isConnected) {
          // A cart section can replace this instance while Shopify is open.
          // Hand the completed sign-in to the current panel without a new sheet.
          const replacement = cartFlow && cartReturn?.id === cartFlow.id ? drawerParts().perks : null;
          if (replacement && replacement !== this && typeof replacement.startNativeAccountFlow === "function") {
            cartHandedOff = true;
            await replacement.startNativeAccountFlow({ activateClub, subscribeIfNeeded, resumeCartReturn: cartFlow });
          }
          return;
        }
        signedIn = true;
        this._hsAuthenticated = true;
        this._hsAuthenticatedEmail = status.email || null;

        if (activateClub) {
          // The server checks current Shopify consent and zero previous orders.
          // Only the separate Subscribe button may change marketing consent.
          await this.activateMember({ scope, button, subscribeIfNeeded });
        } else {
          this.applyMemberStatus(status);
          if (status.subscribed === true) {
            // Let the quiz move past its join step for an existing subscriber.
            // This event does not itself subscribe or issue any reward.
            document.dispatchEvent(new CustomEvent(SUBSCRIBED_EVENT));
          }
        }
      } catch (error) {
        failure = error.message || "We couldn't complete sign-in. Please try again.";
      } finally {
        this._hsAuthInProgress = false;
        this.setButtonBusy(button, false);
        if (cartFlow && !cartHandedOff) finishCartSignIn(cartFlow, signedIn);
        if (this.isConnected) {
          await this.hydrateState();
          if (failure) {
            // Authentication can change which view is visible during the flow.
            // Keep any retry message in that view, not in a now-hidden form.
            const visibleScope = this.querySelector('[data-hs-perks-view]:not([hidden])');
            this.showError(visibleScope || scope, failure);
          }
        }
      }
    }

    async waitForShopifyAuthentication(account, cartFlow = null) {
      const deadline = cartFlow?.expiresAt || Date.now() + 5 * 60 * 1000;
      const canContinue = () => this._hsAuthInProgress && (this.isConnected ||
        (cartFlow && cartReturn?.id === cartFlow.id && drawerParts().perks));
      let sheetClosed = false;
      const onClose = () => { sheetClosed = true; };
      account?.addEventListener?.("close", onClose);

      try {
        while (Date.now() < deadline) {
          if (!canContinue()) break;
          await new Promise((resolve) => setTimeout(resolve, 900));
          if (!canContinue()) break;

          try {
            const status = await this.memberStatus();
            if (status.authenticated) {
              this._hsAuthenticated = true;
              return status;
            }
          } catch (error) {
            // Keep polling while Shopify completes native authentication.
          }

          if (sheetClosed) {
            // Give Shopify one final moment to persist the signed-in storefront
            // session after the native account sheet closes.
            await new Promise((resolve) => setTimeout(resolve, 700));
            try {
              const status = await this.memberStatus();
              if (status.authenticated) {
                this._hsAuthenticated = true;
                return status;
              }
            } catch (error) {}
            break;
          }
        }
      } finally {
        account?.removeEventListener?.("close", onClose);
      }

      return { authenticated: false };
    }

    async activateMember({ scope, button, subscribeIfNeeded = false }) {
      this.clearError(scope);
      this.setButtonBusy(button, true, "Activating perks...");

      const sessionVersion = this._hsSessionVersion || 0;
      const activated = await this.api("/activate-member", {
        method: "POST",
        body: JSON.stringify({
          marketingConsent: subscribeIfNeeded === true,
          consentSource: subscribeIfNeeded ? "promo-subscribe" : "shopify",
          signupMethod: "native-account",
        }),
      });

      if (sessionVersion !== (this._hsSessionVersion || 0)) {
        throw new Error("Your sign-in changed. Please sign in again to view your perks.");
      }
      if (activated.ok && activated.authenticated && activated.welcomeState === "subscription_required") {
        this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
        this.applyMemberStatus(activated);
        const inactiveView = this.querySelector('[data-hs-perks-view="unsubscribed"]');
        this.showError(inactiveView, "Your email subscription is not active. Use Subscribe & unlock rewards below to join the newsletter and activate your perks.");
        return activated;
      }
      if (!activated.ok || !activated.authenticated || !activated.subscribed || !activated.member) {
        throw new Error("We couldn't confirm your High Spirits Club activation. Please try again.");
      }
      this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
      this._hsAuthenticated = true;
      this.clearLegacyGuestState();
      await this.handleActivation(activated);
      return activated;
    }

    async handleActivation(activated) {
      const discountCode = activated.reward?.code;

      if (activated.welcomeState === "already_redeemed") {
        await this.persistGuestCartState("welcome_used");
        document.dispatchEvent(new CustomEvent(SUBSCRIBED_EVENT));
        this.showGuestResult(
          "Email perks active",
          "You've already received your one-time $5 High Spirits Club welcome discount. Your email subscription keeps you eligible for 2.5% cashback, plus an extra $5 on purchases of $100 or more.",
        );
        return;
      }

      if (discountCode && activated.welcomeState === "available") {
        this.showGuestResult(
          "You're in",
          "Your email perks are active. Applying your one-time $5 welcome discount to this cart now...",
        );
        await this.applyDiscountCode(discountCode);
        this.showGuestResult(
          "You're in",
          "Your one-time $5 welcome discount is applied to this cart. You're also subscribed to High Spirits Club emails and eligible for 2.5% cashback, plus an extra $5 on purchases of $100 or more.",
        );
        document.dispatchEvent(new CustomEvent(SUBSCRIBED_EVENT));
        return;
      }

      await this.persistGuestCartState("subscribed");
      document.dispatchEvent(new CustomEvent(SUBSCRIBED_EVENT));
      this.showGuestResult(
        "Email perks active",
        activated.welcomeState === "expired"
          ? "Your one-time $5 welcome discount is no longer available. Your email subscription still keeps you eligible for 2.5% cashback, plus an extra $5 on purchases of $100 or more."
          : activated.welcomeState === "already_claimed"
            ? "You've already received your one-time $5 High Spirits Club welcome discount. Your cashback perks remain active while you're subscribed."
            : activated.welcomeReason === "existing_customer"
              ? "Your cashback perks are active. The one-time $5 welcome discount is for customers with no previous orders who subscribe to the High Spirits Club newsletter."
              : "You're subscribed and eligible for 2.5% cashback as store credit on qualifying purchases, plus an extra $5 on purchases of $100 or more.",
      );
    }

    async activateLoggedInCustomer() {
      if (!this.subscribeButton || this.dataset.state !== "unsubscribed") return;
      return this.startNativeAccountFlow({
        activateClub: true,
        subscribeIfNeeded: true,
        scope: this.querySelector('[data-hs-perks-view="unsubscribed"]'),
        button: this.subscribeButton,
      });
    }

    showGuestResult(title, copy) {
      // A delayed cart update must not re-enable the badge after sign-out.
      if (this._hsAuthenticated !== true || !this.isConnected) return;
      this.setState("subscribed");
      const feedback = this.querySelector("[data-hs-perks-activation-feedback]") || this.resultStep;
      if (!feedback) return;
      feedback.hidden = false;
      feedback.replaceChildren();

      const heading = document.createElement("h4");
      heading.className = "hs-perks__result-title";
      heading.textContent = title;

      const paragraph = document.createElement("p");
      paragraph.className = "hs-perks__result-copy";
      paragraph.textContent = copy;

      feedback.append(heading, paragraph);
    }

    resetGuestFlow() {
      this._hsHydrationVersion = (this._hsHydrationVersion || 0) + 1;
      this.setSignedOutState();
      this.detailsForm?.reset();
      this.clearError(this.detailsStep);
    }

  }

  if (!customElements.get("high-spirits-perks")) {
    customElements.define("high-spirits-perks", HighSpiritsPerks);
  }

  const rehydrateAllPerks = () => {
    document.querySelectorAll("high-spirits-perks, [data-hs-perks]").forEach((element) => {
      if (typeof element.cacheElements === "function") element.cacheElements();
      if (typeof element.positionSurface === "function") element.positionSurface();
      if (typeof element.hydrateState === "function") element.hydrateState();
    });
  };

  const watchedAccounts = new WeakSet();
  const watchAccountComponents = () => {
    document.querySelectorAll("shopify-account").forEach((account) => {
      if (watchedAccounts.has(account)) return;
      watchedAccounts.add(account);
      account.addEventListener("close", () => {
        // Shopify may commit the storefront session just after closing its sheet.
        window.setTimeout(queueRehydrate, 500);
      });
    });
  };

  // Coalesce DOM events in a frame only. There is no status TTL, response cache,
  // or shared request promise: each requested status check still calls the app.
  let rehydrateQueued = false;
  const queueRehydrate = () => {
    if (rehydrateQueued) return;
    rehydrateQueued = true;
    requestAnimationFrame(() => {
      rehydrateQueued = false;
      watchAccountComponents();
      rehydrateAllPerks();
      queueCartReturn();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", queueRehydrate, { once: true });
  } else {
    queueRehydrate();
  }

  window.addEventListener("pageshow", queueRehydrate);
  window.addEventListener("focus", queueRehydrate);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") queueRehydrate();
  });
  document.addEventListener("shopify:section:load", queueRehydrate);
  document.addEventListener("shopify:section:reorder", queueRehydrate);

  // Refresh after real theme-fragment insertions, not after our own title,
  // error, or success-message text changes.
  const observer = new MutationObserver((mutations) => {
    const selector = "high-spirits-perks, [data-hs-perks], shopify-account";
    for (const mutation of mutations) {
      const relevant = Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
      );
      if (relevant) {
        queueRehydrate();
        return;
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
